import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Sparkles } from 'lucide-react';

const Portfolio = () => {
  const [filter, setFilter] = useState('All');
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        elements.forEach((el) => observerRef.current.unobserve(el));
      }
    };
  }, [filter]);

  const projects = [
    {
      id: 1,
      title: 'Fintech Dashboard',
      category: 'Dashboard',
      tech: ['React', 'TypeScript', 'Chart.js', 'TailwindCSS'],
      description: 'A comprehensive financial dashboard with real-time analytics, transaction management, and dark theme support.',
      demoUrl: '/portfolio/fintech-dashboard',
      gradient: 'from-blue-600 via-indigo-600 to-purple-600',
    },
    {
      id: 2,
      title: 'E-commerce Store',
      category: 'E-commerce',
      tech: ['React', 'Node.js', 'Stripe', 'MongoDB'],
      description: 'Full-featured online store with product catalog, shopping cart, checkout flow, and payment integration.',
      demoUrl: '/portfolio/ecommerce-store',
      gradient: 'from-purple-600 via-pink-600 to-rose-600',
    },
    {
      id: 3,
      title: 'Admin Dashboard',
      category: 'Dashboard',
      tech: ['React', 'Material-UI', 'Express', 'PostgreSQL'],
      description: 'Enterprise admin panel with user management, analytics tables, and comprehensive data visualization.',
      demoUrl: '/portfolio/admin-dashboard',
      gradient: 'from-green-600 via-teal-600 to-cyan-600',
    },
    {
      id: 4,
      title: 'Job Board',
      category: 'Platform',
      tech: ['React', 'Node.js', 'MySQL', 'Elasticsearch'],
      description: 'Modern job board platform with advanced search filters, application tracking, and employer dashboard.',
      demoUrl: '/portfolio/job-board',
      gradient: 'from-orange-600 via-red-600 to-pink-600',
    },
    {
      id: 5,
      title: 'Modern Landing Page',
      category: 'Marketing',
      tech: ['Next.js', 'TailwindCSS', 'Framer Motion'],
      description: 'SaaS product landing page with gradient hero section, feature highlights, and conversion-optimized design.',
      demoUrl: '/portfolio/landing-page',
      gradient: 'from-cyan-600 via-blue-600 to-indigo-600',
    },
    {
      id: 6,
      title: 'Social Media Dashboard',
      category: 'Dashboard',
      tech: ['React', 'Chart.js', 'Socket.io', 'Node.js'],
      description: 'Social media management dashboard with post scheduling, analytics, engagement metrics, and real-time updates.',
      demoUrl: '/portfolio/social-dashboard',
      gradient: 'from-violet-600 via-purple-600 to-fuchsia-600',
    },
  ];

  const categories = ['All', ...new Set(projects.map((p) => p.category))];

  const filteredProjects =
    filter === 'All' ? projects : projects.filter((p) => p.category === filter);

  return (
    <div className="pt-16 min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl animate-float"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="text-center mb-12 fade-in">
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkles className="text-primary-600 animate-sparkle" size={24} />
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
              Our <span className="bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">Portfolio</span>
            </h1>
            <Sparkles className="text-primary-600 animate-sparkle" size={24} style={{ animationDelay: '1s' }} />
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore our collection of successful projects across various industries
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12 fade-in">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${
                filter === category
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/50 scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md hover:shadow-lg'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className="group fade-in bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 card-3d border border-gray-100"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={`relative h-48 bg-gradient-to-br ${project.gradient} flex items-center justify-center overflow-hidden`}>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.2),transparent)]"></div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute top-2 right-2 w-2 h-2 bg-yellow-300 rounded-full animate-pulse"></div>
                  <div className="absolute bottom-4 left-4 w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                  <div className="absolute top-1/2 right-4 w-1.5 h-1.5 bg-cyan-300 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                </div>
                <span className="text-white text-xl font-semibold relative z-10 group-hover:scale-110 transition-transform duration-300">
                  {project.title}
                </span>
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
              <div className="p-6">
                <span className="text-sm text-primary-600 font-medium bg-primary-50 px-3 py-1 rounded-full">
                  {project.category}
                </span>
                <h3 className="text-2xl font-bold text-gray-900 mt-3 mb-3 group-hover:text-primary-600 transition-colors">
                  {project.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tech.map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full group-hover:bg-primary-50 group-hover:text-primary-700 transition-colors"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <Link
                  to={project.demoUrl}
                  className="group/link inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-all"
                >
                  View Demo
                  <ExternalLink size={16} className="group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
